import { Dispatch, FC, SetStateAction, useMemo, useState } from 'react';
import styled from 'styled-components';
import { AddModal } from '../../../../../component/modal';
import { colors } from '../../../../../component/colors';
import { DropDownWithLabel } from '../../../../../component/dropDown';
import { Input } from '../../../../../component/input';
import { ImagePicker } from '../../../../../component/imagePicker';
import { useFormik } from 'formik';
import { addPropertyValidation } from '../../../validation';
import { showErrorToast, showSuccessToast } from '../../../../../utils/toast';
import { useAddPropertyMutation } from '../../../../../store/realEstate/api';
import { GooglePlace } from '../../../../../component/googlePlace';
import { useFetchAgentQuery } from '../../../../../store/agent/api';
import { TextAreaWithLabel } from '../../../../../component/textArea';
import { landUnit } from '../../../../../utils/module';
import { SearchableDropDownWithLabel } from '../../../../../component/dropDown/searchableDropDownWthLabel';
import useDebounce from '../../../../../utils/useDebounce';
import { generateUrl } from '../../../../../utils/urlGenerator';
type IAddProperty = {
  show?: boolean;
  setShow: Dispatch<SetStateAction<boolean>>;
};
type FormikValues = {
  category: string;
  price: string;
  type: string;
  noOfRooms: number;
  noOfBath: number;
  roomSize: string;
  agent: {
    name: string;
    index: number;
  };
  description: string;
  title: string;
  inspectionFee: string;
  landUnit: string;
  landSize: number;
  location: {
    address: string;
    state: string;
    lng: number;
    lat: number;
  };
  file: File[] | null;
};
export const AddProperty: FC<IAddProperty> = ({ setShow }) => {
  const [searchAgent, setSearchAgent] = useState('');
  const debounceSearchAgent = useDebounce(searchAgent, 500);
  const { data: agents } = useFetchAgentQuery(
    generateUrl({ module: 'real-estate', search: debounceSearchAgent })
  );
  const [addProperty, { isLoading: isAddingProperty }] =
    useAddPropertyMutation();
  const handleFilePicked = (file: File[]) => {
    setFieldValue('file', file);
  };
  const agentsList = useMemo(() => {
    return (
      agents?.data.fetchedData.map((agents) => ({
        name: agents.name,
        imageUrl: agents.file.url
      })) ?? []
    );
  }, [agents]);

  const handleAddProperty = (values: FormikValues) => {
    if (!agents?.data) return;
    if (!values.file) {
      showErrorToast('image is required');
      return;
    }
    const formData = new FormData();
    formData.append('category', values.category.toLowerCase());
    formData.append('price', values.price);
    formData.append('type', values.type.toLowerCase());
    if (values.type === 'Land') {
      formData.append('landSize[value]', values.landSize.toString());
      formData.append(
        'landSize[unit]',
        values.landUnit.toString().toLowerCase()
      );
    } else {
      formData.append('noOfBath', values.noOfBath.toString());
      formData.append('noOfRooms', values.noOfRooms.toString());
      formData.append('roomSize', values.roomSize);
    }
    formData.append('agent', agents?.data.fetchedData[values.agent.index]._id);
    formData.append('description', values.description);
    formData.append('title', values.title);
    if (values.inspectionFee) {
      formData.append('inspectionFee', values.inspectionFee);
    }
    values.file.forEach((file) => {
      formData.append('file', file);
    });
    formData.append('location[state]', values.location.state);
    formData.append('location[name]', values.location.address);
    formData.append(
      'location[coordinates][longitude]',
      values.location.lng.toString()
    );
    formData.append(
      'location[coordinates][latitude]',
      values.location.lat.toString()
    );
    addProperty(formData)
      .unwrap()
      .then((result) => {
        showSuccessToast(result.message);
        setShow(false);
      })
      .catch((error) => {
        showErrorToast(error.message);
      });
  };
  const { values, touched, handleChange, handleSubmit, setFieldValue, errors } =
    useFormik({
      initialValues: {
        category: '',
        price: '',
        type: '',
        noOfRooms: 0,
        noOfBath: 0,
        roomSize: '',
        agent: { name: '', index: 0 },
        description: '',
        title: '',
        inspectionFee: '',
        landSize: 0,
        landUnit: '',
        location: {
          address: '',
          state: '',
          lng: 0,
          lat: 0
        },
        file: null
      },
      validationSchema: addPropertyValidation,
      onSubmit: handleAddProperty
    });

  const propertyType = useMemo(() => {
    let typeOfProperty = ['Apartment', 'Bungalow', 'Duplex', 'Flat', 'Land'];
    if (values.category === 'Rent') {
      typeOfProperty.splice(4);
    }
    return typeOfProperty;
  }, [values.category]);
  return (
    <AddModal
      title="Add Property"
      disableBtn={isAddingProperty}
      btnTxt={isAddingProperty ? 'Loading...' : 'Add Property'}
      btnClick={handleSubmit}
      closeModal={() => setShow(false)}
      isLoading={isAddingProperty}
    >
      <Container>
        <Row>
          <CustomDropDown
            label="Category"
            value={values.category}
            onSelect={(item) => {
              setFieldValue('category', item);
              setFieldValue('type', '');
            }}
            options={['Buy', 'Lease', 'Rent']}
          />
          <CustomDropDown
            label="Type"
            value={values.type}
            onSelect={(item) => {
              setFieldValue('type', item);
            }}
            options={propertyType}
          />
        </Row>
        <Row>
          <CustomInput
            labelText="Price"
            type="number"
            value={values.price}
            onChange={handleChange('price')}
            placeholder="Enter property price"
            errorMessage={touched.price ? errors.price : ''}
          />
          <CustomInput
            labelText="Title"
            value={values.title}
            onChange={handleChange('title')}
            placeholder="Enter title"
            errorMessage={touched.title ? errors.title : ''}
          />
        </Row>
        <Row>
          <CustomGooglePlace
            onSelect={(value) => setFieldValue('location', value)}
            placeholder="Enter address"
            label="Address"
            errorMessage={
              touched.location && errors.location ? '*required' : ''
            }
          />
          <CustomInput
            labelText="Inspection fee"
            placeholder="Enter Inspection fee"
            errorMessage={touched.inspectionFee ? errors.inspectionFee : ''}
            onChange={handleChange('inspectionFee')}
            value={values.inspectionFee}
          />
        </Row>
        {values.type === 'Land' ? (
          <Row>
            <CustomInput
              labelText="Land size"
              placeholder="Enter land size"
              type={'number'}
              value={values.landSize}
              onChange={handleChange('landSize')}
              errorMessage={touched.landSize ? errors.landSize : ''}
            />
            <CustomDropDown
              label="Land unit"
              placeholder="Choose land unit"
              value={values.landUnit}
              errorMessage={touched.landUnit ? errors.landUnit : ''}
              onSelect={(item) => {
                setFieldValue('landUnit', item);
              }}
              options={landUnit}
            />
          </Row>
        ) : (
          <Row>
            <CustomInput
              labelText="Number of rooms"
              value={values.noOfRooms}
              type="number"
              min={1}
              onChange={handleChange('noOfRooms')}
              errorMessage={touched.noOfRooms ? errors.noOfRooms : ''}
            />
            <CustomInput
              labelText="Number of baths"
              value={values.noOfBath}
              type="number"
              min={1}
              onChange={handleChange('noOfBath')}
              errorMessage={touched.noOfBath ? errors.noOfBath : ''}
            />
          </Row>
        )}
        <Row>
          {values.type !== 'Land' && (
            <CustomInput
              labelText="Room size (sqrm)"
              placeholder="Enter room size"
              value={values.roomSize}
              onChange={handleChange('roomSize')}
              errorMessage={touched.roomSize ? errors.roomSize : ''}
            />
          )}
          <SearchableCustomDropDown
            search={searchAgent}
            setSearch={setSearchAgent}
            label="SOJI agent"
            value={values.agent.name}
            onSelect={(item, index) => {
              setFieldValue('agent', { name: item, index });
            }}
            options={agentsList}
            errorMessage={touched.agent && errors.agent ? '*required' : ''}
          />
        </Row>
        <CustomTextarea
          labelText="Description"
          errorMessage={touched.description ? errors.description : ''}
          placeholder="Enter description"
          value={values.description}
          onChange={handleChange('description')}
        />
        <CustomImagePicker onFilePicked={handleFilePicked} />
      </Container>
    </AddModal>
  );
};

const Container = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: 0px 40px;
  overflow-y: auto;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
`;
const CustomGooglePlace = styled(GooglePlace)`
  flex: 0.46;
  margin-bottom: 16px;
`;
const CustomInput = styled(Input)`
  flex: 0.46;
  color: ${colors.textDark};
  margin-bottom: 16px;
`;
const CustomImagePicker = styled(ImagePicker)`
  margin-bottom: 32px;
`;

const CustomDropDown = styled(DropDownWithLabel)`
  flex: 0.46;
  margin-bottom: 16px;
`;
const CustomTextarea = styled(TextAreaWithLabel)`
  margin-bottom: 16px;
`;
const SearchableCustomDropDown = styled(SearchableDropDownWithLabel)`
  flex: 0.46;
  margin-bottom: 16px;
`;
